import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Card,
  Switch,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  message,
  Space,
  Popconfirm,
  Alert,
  Divider,
  Tag,
  Collapse,
  Grid,
  Tabs,
  Row,
  Col,
  Statistic,
  DatePicker,
  Drawer,
  Tooltip,
  Empty,
  Spin,
  Slider,
  Segmented,
} from 'antd';
import {
  SafetyOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  StopOutlined,
  EyeOutlined,
  ThunderboltOutlined,
  BarChartOutlined,
  UnorderedListOutlined,
  FilterOutlined,
  SearchOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import cdnService from '../../services/cdnService';
import RuleConditionEditor, { normalizeConditions, toExpression, validateConditions } from '../../../shared/components/cdn/RuleConditionEditor';
import {
  WAF_RULESETS,
  CATEGORY_NAMES,
  CATEGORY_ICONS,
  SEVERITY_COLORS,
  SEVERITY_NAMES
} from '../../../shared/constants/wafRules';
import { retentionDaysOf, retentionDisabledDate, clampRange, RetentionHint } from '../../../shared/components/cdn/PlanGate';
import dayjs from 'dayjs';
import * as echarts from 'echarts';
import TagsInput, { isIpOrCidr, isCountryCode, isRegionCode, isAsn } from '../../../shared/components/TagsInput';
import CountryFlag, { CountryFlagList } from '../../../shared/components/CountryFlag';
import { DurationInput, SizeInput, CountInput, RateLimitPresets } from '../../../shared/components/cdn/UnitInputs';

// 防护强度预设:滑块每一档 = (paranoia level, 异常分阈值)
const WAF_STRENGTH_LEVELS = [
  { label: 'Relaxed', pl: 1, th: 10, desc: 'Blocks only obvious attacks; almost no false positives' },
  { label: 'Standard', pl: 1, th: 5, desc: 'Blocks common attacks (SQL injection, XSS, path traversal, etc.); suits most sites (recommended)' },
  { label: 'Enhanced', pl: 2, th: 5, desc: 'More rules; complex forms or APIs may occasionally be blocked by mistake' },
  { label: 'Strict', pl: 3, th: 5, desc: 'Catches subtler evasion techniques, with noticeably more false positives; try "Log only" first' },
  { label: 'Maximum', pl: 4, th: 5, desc: 'Highest detection and the most false positives; for API sites with fixed request formats' },
];

// WAF 分类(后端 WafCategoryCatalog 的 14 类):名称 + 一行说明;规则数/开关来自 API
const WAF_CATEGORY_META = {
  sqli: { name: 'SQL injection', desc: 'Stops attempts to read or change your database through request input' },
  xss: { name: 'Cross-site scripting (XSS)', desc: 'Stops scripts injected into your pages through request input' },
  rce: { name: 'Remote command execution', desc: 'Stops shell commands smuggled into requests' },
  lfi_rfi: { name: 'File inclusion (LFI/RFI)', desc: 'Stops path traversal and loading of local or remote files' },
  php: { name: 'PHP injection', desc: 'Stops PHP code and function abuse in requests' },
  java: { name: 'Java attacks', desc: 'Stops Java deserialization, expression and Log4Shell-style attacks' },
  webshell: { name: 'Webshells and backdoors', desc: 'Detects backdoor uploads and known webshell pages' },
  generic: { name: 'Generic attacks', desc: 'Stops Node.js, template injection and other generic payloads' },
  session_fixation: { name: 'Session fixation', desc: 'Stops attempts to force a session ID on your visitors' },
  protocol_enforcement: { name: 'Protocol compliance', desc: 'Rejects malformed or non-standard HTTP requests' },
  protocol_attack: { name: 'Protocol attacks', desc: 'Stops request smuggling, header injection and multipart tricks' },
  scanner: { name: 'Scanner detection', desc: 'Stops known vulnerability scanners' },
  data_leakage: { name: 'Data leakage', desc: 'Stops error details and source code from leaking in responses' },
  bad_bots: { name: 'Bad bots', desc: 'Stops known malicious crawlers and bots' },
};

const { Panel } = Collapse;
const { useBreakpoint } = Grid;
const { RangePicker } = DatePicker;
const { TextArea } = Input;

// 动作颜色映射
const ACTION_COLORS = {
  block: 'red',
  allow: 'green',
  js_challenge: 'orange',
  managed_challenge: 'gold',
  log: 'blue',
  ratelimit: 'purple',
};

const ACTION_LABELS = {
  block: '拦截',
  allow: '放行',
  js_challenge: 'JS 质询',
  managed_challenge: '托管质询',
  log: '仅记录',
  ratelimit: '速率限制',
};

// 自定义 WAF 规则（cdn_waf_config.custom_rules）—— CRS 风格评分规则，
// 与上方通用「防火墙规则」(firewall_rules 表) 分离：这里的规则命中后按分值累加异常分，
// 达到异常分阈值即拦截，结构对齐 waf_rulesets.rules 与 Rust wafcrs 引擎。
const WAF_MATCH_COLLECTIONS = [
  { value: 'URI', label: 'URI（含查询串）' },
  { value: 'PATH', label: 'PATH（路径）' },
  { value: 'QS_ARGS', label: '查询参数' },
  { value: 'BODY', label: '请求体' },
  { value: 'HEADER', label: '请求头' },
  { value: 'COOKIE', label: 'Cookie' },
  { value: 'USER_AGENT', label: 'User-Agent' },
  { value: 'METHOD', label: '请求方法' },
];

const WAF_MATCH_TYPES = [
  { value: 'regex', label: '正则匹配' },
  { value: 'detect_sqli', label: 'SQL injection (smart detection)' },
  { value: 'detect_xss', label: 'XSS (smart detection)' },
];

// 自定义 WAF 规则动作(与边缘 wafsig 同义):block 直接拦截、score 累加异常分(达阈值才拦)、log 只记录
const WAF_CUSTOM_ACTIONS = [
  { value: 'block', label: 'Block', color: 'red' },
  { value: 'score', label: 'Add score', color: 'orange' },
  { value: 'log', label: 'Log only', color: 'blue' },
];

export default function Firewall() {
  const { t } = useTranslation();
  const { domainData, reloadDomain } = useOutletContext();
  // 套餐日志保留天数:WAF 分析/日志只能选保留期内
  const retDays = retentionDaysOf(domainData);
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [activeTab, setActiveTab] = useState('rules');

  // WAF 预设规则状态
  const [loading, setLoading] = useState(false);
  const [wafEnabled, setWafEnabled] = useState(false);
  const [wafConfig, setWafConfig] = useState({});
  const [enabledRulesets, setEnabledRulesets] = useState(['sqli', 'xss']);
  // WAF 分类开关(CRS 已开的域按 14 类展示;未开的域仍显示基础签名规则集)
  const [wafCategories, setWafCategories] = useState(null);
  const [categoryPatch, setCategoryPatch] = useState({});
  const [rulesetsDirty, setRulesetsDirty] = useState(false);
  const [availableRulesets, setAvailableRulesets] = useState({});
  const [rulesetsVersion, setRulesetsVersion] = useState(1);

  // 自定义 WAF 评分规则（custom_rules）状态
  const [customRules, setCustomRules] = useState([]);
  const [crModalVisible, setCrModalVisible] = useState(false);
  const [editingCrIndex, setEditingCrIndex] = useState(null);
  const [crSaving, setCrSaving] = useState(false);
  const [wafToggling, setWafToggling] = useState(false);
  const [crForm] = Form.useForm();

  // 防火墙规则状态
  const [fwRules, setFwRules] = useState([]);
  const [fwLoading, setFwLoading] = useState(false);
  const [fwModalVisible, setFwModalVisible] = useState(false);
  const [fwCondError, setFwCondError] = useState(null);
  const [editingRule, setEditingRule] = useState(null);
  const [fwForm] = Form.useForm();

  // 安全区（专用防盗链 / 地区封禁 / UA 名单 + 访问鉴权）状态
  const [secForm] = Form.useForm();
  const [secSaving, setSecSaving] = useState(false);
  const authMethodsWatch = Form.useWatch(['auth', 'methods'], secForm);
  const uamModeWatch = Form.useWatch(['uam', 'mode'], secForm);
  const denyCountriesWatch = Form.useWatch(['geo', 'deny_countries'], secForm);
  const allowCountriesWatch = Form.useWatch(['geo', 'allow_countries'], secForm);

  // WAF 分析状态
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsRange, setAnalyticsRange] = useState([dayjs().subtract(24, 'hour'), dayjs()]);
  const [overview, setOverview] = useState({});
  const [timeline, setTimeline] = useState([]);
  const [topRules, setTopRules] = useState([]);
  const [topIPs, setTopIPs] = useState([]);
  const timelineChartRef = useRef(null);
  const timelineChartInstance = useRef(null);

  // 事件日志状态
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsRange, setLogsRange] = useState([dayjs().subtract(24, 'hour'), dayjs()]);
  const [logs, setLogs] = useState([]);
  const [logFilters, setLogFilters] = useState({});
  const [logsPage, setLogsPage] = useState(1);
  const [logsPageSize, setLogsPageSize] = useState(50);
  const [logsTotal, setLogsTotal] = useState(0);

  useEffect(() => {
    if (domainData) {
      setWafEnabled(!!domainData.waf_enabled);
      loadWafRulesets();
      loadWafConfig();
      loadWafCategories();
      loadFirewallRules();
      secForm.setFieldsValue(securityToForm(domainData?.settings?.security));
    }
  }, [domainData]);

  useEffect(() => {
    if (activeTab === 'analytics' && domainData) {
      loadAnalytics();
    }
  }, [activeTab, analyticsRange]);

  useEffect(() => {
    if (activeTab === 'logs' && domainData) {
      loadLogs();
    }
  }, [activeTab, logsRange, logFilters, logsPage, logsPageSize]);

  // 过滤条件或时间范围变化时重置到第一页
  useEffect(() => {
    setLogsPage(1);
  }, [logsRange, logFilters]);

  // 时间线图表
  useEffect(() => {
    let cleanupResize;
    if (timeline.length > 0 && timelineChartRef.current) {
      cleanupResize = renderTimelineChart();
    }
    return () => {
      if (cleanupResize) {
        cleanupResize();
      }
      if (timelineChartInstance.current) {
        timelineChartInstance.current.dispose();
      }
    };
  }, [timeline]);

  // ===================== WAF 预设规则 =====================

  // 套餐允许的规则集(null = 全部);不含的显示为需升级,不可开
  const [allowedRulesets, setAllowedRulesets] = useState(null);

  const loadWafRulesets = async () => {
    try {
      const response = await cdnService.getWafRulesetsGrouped(domainData?.id);
      if (response.success && response.data) {
        setAllowedRulesets(Array.isArray(response.allowed_rulesets) ? response.allowed_rulesets : null);
        const rulesetsMap = {};
        Object.entries(response.data).forEach(([category, rulesets]) => {
          rulesets.forEach(ruleset => {
            rulesetsMap[ruleset.key] = { ...ruleset };
          });
        });
        setAvailableRulesets(rulesetsMap);
        setRulesetsVersion(response.version || 1);
      } else {
        setAvailableRulesets(WAF_RULESETS);
      }
    } catch (error) {
      setAvailableRulesets(WAF_RULESETS);
    }
  };

  const loadWafConfig = async () => {
    setLoading(true);
    try {
      const response = await cdnService.getWafConfig(domainData.id);
      if (response.success && response.data) {
        setEnabledRulesets(response.data.enabled_rulesets || ['sqli', 'xss']);
        setCustomRules(Array.isArray(response.data.custom_rules) ? response.data.custom_rules : []);
        setWafConfig(response.data);
        setRulesetsDirty(false);
      }
    } catch (error) {
      console.error('Failed to load WAF config:', error);
      message.error(t('Failed to load WAF configuration'));
    } finally {
      setLoading(false);
    }
  };

  const loadWafCategories = async () => {
    const response = await cdnService.getWafCategories(domainData.id);
    if (response.success && response.data) {
      setWafCategories(response.data);
      setCategoryPatch({});
    }
  };

  const handleCategoryToggle = (key, checked) => {
    const orig = (wafCategories?.categories || []).find((c) => c.key === key)?.enabled;
    setCategoryPatch((prev) => {
      const next = { ...prev };
      if (orig === checked) delete next[key];
      else next[key] = checked;
      return next;
    });
    setRulesetsDirty(true);
  };

  const handleWafToggle = async (checked) => {
    if (wafToggling) return;
    setWafToggling(true);
    try {
      const response = await cdnService.updateDomain({
        id: domainData.id,
        waf_enabled: !!checked
      });
      if (response.success) {
        setWafEnabled(checked);
        message.success(checked ? t('WAF enabled') : t('WAF disabled'));
        reloadDomain();
      } else {
        message.error(response.error || t('Operation failed'));
      }
    } catch (error) {
      message.error(t('Operation failed'));
    } finally {
      setWafToggling(false);
    }
  };

  const handleRulesetToggle = (rulesetKey, checked) => {
    setEnabledRulesets(checked
      ? [...enabledRulesets, rulesetKey]
      : enabledRulesets.filter(key => key !== rulesetKey));
    setRulesetsDirty(true);
  };

  const handleSaveConfig = async () => {
    setLoading(true);
    try {
      const response = await cdnService.saveWafConfig({
        domain_id: domainData.id,
        enabled_rulesets: enabledRulesets,
        mode: wafConfig.mode || 'active',
        score_threshold: wafConfig.score_threshold || 5,
        // CRS 规则集开关：paranoia level（1-4）+ 异常分阈值，对齐 Rust wafcrs CrsConfig。
        paranoia_level: wafConfig.paranoia_level || 1,
        // 自定义评分规则一并回写，避免保存规则集时把已存的 custom_rules 清空。
        custom_rules: customRules,
      });
      if (response.success) {
        // 分类开关在规则集之后保存(bad_bots 会在服务端改 enabled_rulesets,不能被上面覆盖)
        if (Object.keys(categoryPatch).length > 0) {
          const res = await cdnService.saveWafCategories(domainData.id, categoryPatch);
          if (!res.success) {
            message.error(res.error || t('Failed to save'));
            return;
          }
        }
        message.success(t('WAF configuration saved'));
        loadWafConfig();
        loadWafCategories();
      } else {
        message.error(response.error || t('Failed to save'));
      }
    } catch (error) {
      message.error(t('Failed to save'));
    } finally {
      setLoading(false);
    }
  };

  // CRS 已开 → 按分类展示;规则数随当前(含未保存的)防护强度 PL 变化
  const crsActive = !!wafCategories?.crs_enabled;
  const curPl = wafConfig.paranoia_level || 1;
  const categoryList = (wafCategories?.categories || []).map((c) => ({
    ...c,
    on: categoryPatch[c.key] ?? c.enabled,
    count: c.rule_count_by_pl?.[curPl] ?? c.rule_count,
  }));

  // 14 个 CRS 分类按原大分类分组展示(折叠面板 + 小项开关,与基础规则集同一套样式)
  const CRS_CATEGORY_GROUPS = [
    ['injection', ['sqli', 'xss', 'rce', 'php', 'java', 'generic', 'session_fixation']],
    ['file', ['lfi_rfi']],
    ['protocol', ['protocol_enforcement', 'protocol_attack']],
    ['bots', ['scanner', 'bad_bots']],
    // 检查响应内容的两类(需缓冲响应前 64 KB)
    ['response', ['webshell', 'data_leakage']],
  ];

  // 规则小项:紧凑卡片,手风琴里一行多个(名称+开关一行,说明最多两行,规则数一行)
  const renderWafTile = ({ key, name, desc, tip, extra, count, on, onToggle, locked }) => (
    <Col key={key} xs={24} sm={12} xl={8} xxl={6}>
      <Card
        size="small"
        style={{ height: '100%', borderLeft: `3px solid ${on ? '#52c41a' : '#d9d9d9'}` }}
        styles={{ body: { padding: '10px 12px' } }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <strong style={{ flex: 1, minWidth: 0, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</strong>
          {tip && (
            <Tooltip title={tip}>
              <InfoCircleOutlined style={{ color: '#999' }} />
            </Tooltip>
          )}
          {locked
            ? <Tooltip title={t('Available on Pro plans and above')}><Tag color="gold" style={{ marginInlineEnd: 0 }}>Pro</Tag></Tooltip>
            : <Switch size="small" checked={on} onChange={onToggle} disabled={!wafEnabled} />}
        </div>
        <Tooltip title={desc} mouseEnterDelay={0.6}>
          <div style={{ color: '#888', fontSize: 12, marginTop: 4, lineHeight: '18px', height: 36, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{desc}</div>
        </Tooltip>
        <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#999' }}>
          <span>{count === 1 ? t('1 rule') : t('{{n}} rules', { n: count })}</span>
          {extra}
        </div>
      </Card>
    </Col>
  );

  const renderCategoryGrid = () => {
    const byKey = Object.fromEntries(categoryList.map((c) => [c.key, c]));
    const grouped = new Set(CRS_CATEGORY_GROUPS.flatMap(([, keys]) => keys));
    const groups = CRS_CATEGORY_GROUPS.map(([g, keys]) => [g, keys.map((k) => byKey[k]).filter(Boolean)]);
    const rest = categoryList.filter((c) => !grouped.has(c.key));
    if (rest.length) groups.push(['additional', rest]);
    return (
      <Collapse defaultActiveKey={['injection']} ghost>
        {groups.filter(([, items]) => items.length).map(([g, items]) => (
          <Panel
            key={g}
            header={
              <Space wrap size={isMobile ? 'small' : 'middle'}>
                <span style={{ fontSize: isMobile ? '14px' : '18px' }}>{CATEGORY_ICONS[g]}</span>
                <strong>{t(CATEGORY_NAMES[g])}</strong>
                <Tag color="blue">{items.filter((c) => c.on).length}/{items.length}</Tag>
              </Space>
            }
          >
            <Row gutter={[12, 12]}>
              {items.map((c) => {
                const meta = WAF_CATEGORY_META[c.key] || { name: c.key, desc: '' };
                return renderWafTile({
                  key: c.key, name: t(meta.name), desc: t(meta.desc), tip: meta.tip && t(meta.tip),
                  count: c.count, on: c.on, onToggle: (v) => handleCategoryToggle(c.key, v),
                });
              })}
            </Row>
          </Panel>
        ))}
      </Collapse>
    );
  };

  const rulesetsByCategory = React.useMemo(() => {
    const grouped = {};
    Object.entries(availableRulesets).forEach(([key, ruleset]) => {
      if (!grouped[ruleset.category]) grouped[ruleset.category] = [];
      grouped[ruleset.category].push({ key, ...ruleset });
    });
    return grouped;
  }, [availableRulesets]);

  // ===================== 自定义 WAF 评分规则 CRUD (custom_rules) =====================

  // 用户自定义规则 ID 段从 100000 起，避开内置规则集的 1000-9999。
  const nextCustomRuleId = () => {
    const ids = customRules.map(r => Number(r.rule_id) || 0);
    const max = ids.length ? Math.max(...ids) : 0;
    return Math.max(100000, max + 1);
  };

  // 把本地 custom_rules 数组持久化（复用 waf-config/save，携带当前配置避免覆盖其它字段）。
  const persistCustomRules = async (rules) => {
    setCrSaving(true);
    try {
      const response = await cdnService.saveWafConfig({
        domain_id: domainData.id,
        enabled_rulesets: enabledRulesets,
        mode: wafConfig.mode || 'active',
        score_threshold: wafConfig.score_threshold || 5,
        paranoia_level: wafConfig.paranoia_level || 1,
        custom_rules: rules,
      });
      if (response.success) {
        setCustomRules(rules);
        message.success(t('Custom rules saved'));
        return true;
      }
      message.error(response.error || t('Failed to save'));
      return false;
    } catch (error) {
      message.error(t('Failed to save'));
      return false;
    } finally {
      setCrSaving(false);
    }
  };

  const handleCreateCustomRule = () => {
    setEditingCrIndex(null);
    crForm.resetFields();
    crForm.setFieldsValue({
      enabled: true,
      collection: 'URI',
      match_type: 'regex',
      pattern: '',
      action_type: 'block',
      score: 5,
      status: 403,
      message: '',
    });
    setCrModalVisible(true);
  };

  const handleEditCustomRule = (rule, index) => {
    setEditingCrIndex(index);
    const m = rule.match || {};
    const a = rule.action || {};
    crForm.setFieldsValue({
      rule_id: rule.rule_id,
      enabled: rule.enabled !== false,
      collection: m.collection || 'URI',
      var: m.var || '',
      match_type: m.type || 'regex',
      pattern: m.pattern || '',
      action_type: a.type || 'block',
      score: a.score ?? 5,
      status: a.status ?? 403,
      message: a.message || '',
    });
    setCrModalVisible(true);
  };

  const handleSaveCustomRule = async () => {
    try {
      const values = await crForm.validateFields();
      const match = {
        type: values.match_type,
        collection: values.collection,
      };
      if (['HEADER', 'COOKIE'].includes(values.collection) && values.var) {
        match.var = values.var;
      }
      if (values.match_type === 'regex') {
        match.pattern = values.pattern;
      }
      const action = {
        type: values.action_type,
        log: true,
      };
      if (values.action_type === 'score') {
        action.score = values.score ?? 5;
      }
      if (values.action_type === 'block') {
        action.status = values.status || 403;
      }
      if (values.message) action.message = values.message;

      const rule = {
        rule_id: values.rule_id || nextCustomRuleId(),
        phase: 'request',
        enabled: values.enabled !== false,
        match,
        action,
      };

      const next = [...customRules];
      if (editingCrIndex != null) {
        next[editingCrIndex] = rule;
      } else {
        next.push(rule);
      }
      const ok = await persistCustomRules(next);
      if (ok) setCrModalVisible(false);
    } catch (error) {
      if (error && error.errorFields) return; // form validation
      message.error(t('Operation failed'));
    }
  };

  const handleDeleteCustomRule = async (index) => {
    const next = customRules.filter((_, i) => i !== index);
    await persistCustomRules(next);
  };

  const handleToggleCustomRule = async (index, enabled) => {
    const next = customRules.map((r, i) => (i === index ? { ...r, enabled } : r));
    await persistCustomRules(next);
  };

  const crCollectionWatch = Form.useWatch('collection', crForm);
  const crMatchTypeWatch = Form.useWatch('match_type', crForm);
  const crActionWatch = Form.useWatch('action_type', crForm);

  // ===================== 防火墙规则 CRUD =====================

  const loadFirewallRules = async () => {
    setFwLoading(true);
    try {
      const response = await cdnService.getFirewallRules({ domain_id: domainData.id });
      if (response.success) {
        setFwRules(response.data || []);
      }
    } catch (error) {
      console.error('Failed to load firewall rules:', error);
      message.error(t('Failed to load firewall rules'));
    } finally {
      setFwLoading(false);
    }
  };

  const handleCreateRule = () => {
    setEditingRule(null);
    fwForm.resetFields();
    fwForm.setFieldsValue({
      action: 'block',
      priority: 100,
      enabled: true,
      expression: { op: 'and', conditions: [{ collection: 'IP', name: '', op: 'in', var: [] }] },
      action_config: {},
    });
    setFwCondError(null);
    setFwModalVisible(true);
  };

  const handleEditRule = (rule) => {
    setEditingRule(rule);
    // 限速富参：旧数据仅有 key（ip/country/uri），映射到新的 key_by 维度。
    const actionConfig = { ...(rule.action_config || {}) };
    if (rule.action === 'ratelimit' && actionConfig.key_by == null && actionConfig.key != null) {
      actionConfig.key_by = actionConfig.key;
      delete actionConfig.key;
    }
    fwForm.setFieldsValue({
      name: rule.name,
      description: rule.description,
      action: rule.action,
      priority: rule.priority,
      enabled: rule.enabled,
      expression: normalizeConditions(rule.expression),
      action_config: actionConfig,
    });
    setFwCondError(null);
    setFwModalVisible(true);
  };

  const handleSaveRule = async () => {
    try {
      const condErr = validateConditions(fwForm.getFieldValue('expression'));
      setFwCondError(condErr);
      const values = await fwForm.validateFields();
      if (condErr) return;

      const expression = toExpression(values.expression);

      const payload = {
        domain_id: domainData.id,
        name: values.name,
        description: values.description,
        expression,
        action: values.action,
        action_config: values.action_config || {},
        priority: values.priority || 100,
        enabled: values.enabled !== false,
      };

      let response;
      if (editingRule) {
        response = await cdnService.updateFirewallRule({ id: editingRule.id, ...payload });
      } else {
        response = await cdnService.createFirewallRule(payload);
      }

      if (response.success) {
        message.success(editingRule ? t('Rule updated') : t('Rule created'));
        setFwModalVisible(false);
        loadFirewallRules();
      } else {
        message.error(response.error || t('Operation failed'));
      }
    } catch (error) {
      if (error.errorFields) return; // form validation
      message.error(t('Operation failed'));
    }
  };

  const handleDeleteRule = async (id) => {
    try {
      const response = await cdnService.deleteFirewallRule({ id });
      if (response.success) {
        message.success(t('Rule deleted'));
        loadFirewallRules();
      } else {
        message.error(response.error || t('Deletion failed'));
      }
    } catch (error) {
      message.error(t('Deletion failed'));
    }
  };

  const handleToggleRule = async (rule, enabled) => {
    try {
      const response = await cdnService.updateFirewallRule({ id: rule.id, enabled });
      if (response.success) {
        message.success(enabled ? t('Rule enabled') : t('Rule disabled'));
        loadFirewallRules();
      } else {
        message.error(response.error || t('Operation failed'));
      }
    } catch (error) {
      message.error(t('Operation failed'));
    }
  };

  // ===================== WAF 分析 =====================

  const loadAnalytics = async () => {
    if (!analyticsRange || analyticsRange.length < 2) return;
    setAnalyticsLoading(true);
    try {
      const params = {
        domain_id: domainData.id,
        startTime: analyticsRange[0].unix(),
        endTime: analyticsRange[1].unix(),
      };

      const [overviewRes, timelineRes, topRulesRes, topIPsRes] = await Promise.all([
        cdnService.getWafOverview(params),
        cdnService.getWafTimeline(params),
        cdnService.getWafTopRules(params),
        cdnService.getWafTopIPs(params),
      ]);

      if (overviewRes.success) setOverview(overviewRes.data || {});
      if (timelineRes.success) setTimeline(timelineRes.data || []);
      if (topRulesRes.success) setTopRules(topRulesRes.data || []);
      if (topIPsRes.success) setTopIPs(topIPsRes.data || []);
    } catch (error) {
      console.error('Failed to load WAF analytics:', error);
      message.error(t('Failed to load WAF analytics data'));
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const renderTimelineChart = () => {
    if (!timelineChartRef.current) return;
    if (timelineChartInstance.current) {
      timelineChartInstance.current.dispose();
    }
    const chart = echarts.init(timelineChartRef.current);
    timelineChartInstance.current = chart;

    // 按 fw_action 分组
    const actionGroups = {};
    timeline.forEach(item => {
      const action = item.fw_action || 'unknown';
      if (!actionGroups[action]) actionGroups[action] = [];
      actionGroups[action].push({
        time: item.time_bucket,
        count: item.count,
      });
    });

    const colorMap = {
      block: '#ff4d4f',
      allow: '#52c41a',
      js_challenge: '#fa8c16',
      managed_challenge: '#faad14',
      log: '#1890ff',
      ratelimit: '#722ed1',
    };

    const series = Object.entries(actionGroups).map(([action, data]) => ({
      name: t(ACTION_LABELS[action] || action),
      type: 'line',
      smooth: true,
      areaStyle: { opacity: 0.15 },
      data: data.map(d => [d.time, d.count]),
      itemStyle: { color: colorMap[action] || '#666' },
    }));

    chart.setOption({
      tooltip: { trigger: 'axis' },
      legend: { data: series.map(s => s.name), bottom: 0 },
      grid: { left: 50, right: 20, top: 20, bottom: 40 },
      xAxis: { type: 'time' },
      yAxis: { type: 'value', minInterval: 1 },
      series,
    });

    const handleResize = () => chart.resize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  };

  // ===================== 事件日志 =====================

  const loadLogs = async () => {
    if (!logsRange || logsRange.length < 2) return;
    setLogsLoading(true);
    try {
      const params = {
        domain_id: domainData.id,
        startTime: logsRange[0].unix(),
        endTime: logsRange[1].unix(),
        page: logsPage,
        pageSize: logsPageSize,
        ...logFilters,
      };
      const response = await cdnService.getWafLogs(params);
      if (response.success) {
        setLogs(response.data || []);
        setLogsTotal(response.total || 0);
      }
    } catch (error) {
      console.error('Failed to load WAF logs:', error);
      message.error(t('Failed to load WAF logs'));
    } finally {
      setLogsLoading(false);
    }
  };

  // ===================== 条件摘要 =====================

  const getExpressionSummary = (expression) => {
    if (!expression?.conditions) return '-';
    return expression.conditions.map(c => {
      const val = Array.isArray(c.var) ? c.var.slice(0, 3).join(', ') + (c.var.length > 3 ? '...' : '') : (c.var || '');
      const keyPart = c.key ? `[${c.key}]` : '';
      return `${c.collection}${keyPart} ${c.op} ${val}`;
    }).join(' AND ');
  };

  // ===================== Tab 1: 规则配置 =====================

  const fwRuleColumns = [
    {
      title: t('Priority'),
      dataIndex: 'priority',
      width: 80,
      sorter: (a, b) => a.priority - b.priority,
    },
    {
      title: t('Name'),
      dataIndex: 'name',
      ellipsis: true,
    },
    {
      title: t('Match conditions'),
      dataIndex: 'expression',
      ellipsis: true,
      render: (expr) => (
        <Tooltip title={getExpressionSummary(expr)}>
          <span style={{ fontSize: 12, color: '#666' }}>{getExpressionSummary(expr)}</span>
        </Tooltip>
      ),
    },
    {
      title: t('Action'),
      dataIndex: 'action',
      width: 100,
      render: (action) => (
        <Tag color={ACTION_COLORS[action]}>{t(ACTION_LABELS[action] || action)}</Tag>
      ),
    },
    {
      title: t('Enable'),
      dataIndex: 'enabled',
      width: 70,
      render: (enabled, record) => (
        <Switch
          size="small"
          checked={enabled}
          onChange={(v) => handleToggleRule(record, v)}
        />
      ),
    },
    {
      title: t('Actions'),
      width: 100,
      render: (_, record) => (
        <Space size="small">
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEditRule(record)} />
          <Popconfirm title={t('Delete this rule?')} onConfirm={() => handleDeleteRule(record.id)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // 自定义 WAF 评分规则 —— 表格列
  const customRuleColumns = [
    { title: 'ID', dataIndex: 'rule_id', width: 90 },
    {
      title: t('Match'),
      key: 'match',
      ellipsis: true,
      render: (_, r) => {
        const m = r.match || {};
        const coll = m.var ? `${m.collection}[${m.var}]` : m.collection;
        const type = t((WAF_MATCH_TYPES.find(item => item.value === m.type) || {}).label || m.type);
        return (
          <Tooltip title={m.pattern || type}>
            <span style={{ fontSize: 12, color: '#666' }}>{coll} · {m.type === 'regex' ? m.pattern : type}</span>
          </Tooltip>
        );
      },
    },
    {
      title: t('Action'),
      key: 'action',
      width: 110,
      render: (_, r) => {
        const a = r.action || {};
        return (
          <Space size={4}>
            {(() => {
              const opt = WAF_CUSTOM_ACTIONS.find((o) => o.value === a.type) || WAF_CUSTOM_ACTIONS[0];
              return <Tag color={opt.color}>{t(opt.label)}</Tag>;
            })()}
            {a.type === 'score' && <Tag>+{a.score ?? 0}</Tag>}
          </Space>
        );
      },
    },
    {
      title: t('Enable'),
      dataIndex: 'enabled',
      width: 70,
      render: (enabled, record, index) => (
        <Switch size="small" checked={enabled !== false} onChange={(v) => handleToggleCustomRule(index, v)} loading={crSaving} />
      ),
    },
    {
      title: t('Actions'),
      width: 100,
      render: (_, record, index) => (
        <Space size="small">
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEditCustomRule(record, index)} />
          <Popconfirm title={t('Delete this rule?')} onConfirm={() => handleDeleteCustomRule(index)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const renderCustomRulesCard = () => (
    <Card
      title={<Space><ThunderboltOutlined /> {isMobile ? t('Custom rules') : t('Custom WAF rules (scoring)')}</Space>}
      extra={
        <Button type="primary" icon={<PlusOutlined />} size={isMobile ? 'small' : 'middle'} onClick={handleCreateCustomRule} disabled={!wafEnabled}>{t('Add rule')}</Button>
      }
      size={isMobile ? 'small' : 'default'}
      style={{ marginTop: 24 }}
    >
      <Alert
        message={t('When a custom scoring rule matches, its score is added to the request\'s anomaly score. The request is blocked when the Anomaly score threshold above is reached. Use these rules to add targeted checks beyond the built-in rulesets.')}
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />
      <Table
        dataSource={customRules.map((r, i) => ({ ...r, __index: i }))}
        columns={isMobile ? customRuleColumns.filter(c => ['ID', t('Action'), t('Actions')].includes(c.title)) : customRuleColumns}
        rowKey={(r) => r.rule_id ?? r.__index}
        size="small"
        pagination={false}
        locale={{ emptyText: <Empty description={t('No custom rules yet. Click "Add" in the top right.')} /> }}
      />
    </Card>
  );

  const renderCustomRuleModal = () => (
    <Modal
      title={editingCrIndex != null ? t('Edit custom WAF rule') : t('Add custom WAF rule')}
      open={crModalVisible}
      onOk={handleSaveCustomRule}
      confirmLoading={crSaving}
      onCancel={() => setCrModalVisible(false)}
      width={isMobile ? '95%' : 640}
      destroyOnClose
    >
      <Form form={crForm} layout="vertical">
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="rule_id" label={t('Rule ID')} tooltip={t('Leave blank to assign automatically (starting at 100000)')}>
              <InputNumber min={1} style={{ width: '100%' }} placeholder={t('Automatic allocation')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="enabled" label={t('Enable')} valuePropName="checked" initialValue={true}>
              <Switch checkedChildren={t('Enable')} unCheckedChildren={t('Disable')} />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left" style={{ margin: '8px 0' }}>{t('Match')}</Divider>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="collection" label={t('Check location')} rules={[{ required: true }]}>
              <Select options={WAF_MATCH_COLLECTIONS.map(option => ({ ...option, label: t(option.label) }))} />
            </Form.Item>
          </Col>
          {['HEADER', 'COOKIE'].includes(crCollectionWatch) && (
            <Col span={12}>
              <Form.Item name="var" label={crCollectionWatch === 'HEADER' ? t('Request header name') : t('Cookie name')} rules={[{ required: true, message: t('Please enter a name') }]}>
                <Input placeholder={crCollectionWatch === 'HEADER' ? t('e.g. X-Forwarded-For') : t('e.g. session')} />
              </Form.Item>
            </Col>
          )}
        </Row>
        <Form.Item name="match_type" label={t('Match method')} rules={[{ required: true }]}>
          <Select options={WAF_MATCH_TYPES.map(option => ({ ...option, label: t(option.label) }))} />
        </Form.Item>
        {crMatchTypeWatch === 'regex' && (
          <Form.Item name="pattern" label={t('Regular expression')} rules={[{ required: true, message: t('Enter a regular expression') }]}>
            <TextArea rows={2} placeholder={t('e.g. (?i)union\\s+select')} autoSize={{ minRows: 2, maxRows: 4 }} />
          </Form.Item>
        )}

        <Divider orientation="left" style={{ margin: '8px 0' }}>{t('Action')}</Divider>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="action_type" label={t('Action')} rules={[{ required: true }]} initialValue="block">
              <Select options={WAF_CUSTOM_ACTIONS.map(option => ({ ...option, label: t(option.label) }))} />
            </Form.Item>
          </Col>
          {crActionWatch === 'score' && (
            <Col span={16}>
              <Form.Item
                name="score"
                label={t('Points')}
                initialValue={5}
                extra={t('Scores from matching rules add up; the request is blocked at {{n}} points', { n: wafConfig.score_threshold || 5 })}
              >
                <InputNumber min={1} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          )}
          {crActionWatch === 'block' && (
            <Col span={8}>
              <Form.Item name="status" label={t('Block status code')} initialValue={403}>
                <InputNumber min={400} max={599} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          )}
        </Row>
        <Form.Item name="message" label={t('Message')} tooltip={t('Description text recorded in WAF logs')}>
          <Input placeholder={t('e.g. Custom SQLi rule')} />
        </Form.Item>
      </Form>
    </Modal>
  );

  const renderRulesTab = () => (
    <div>
      {/* 自定义防火墙规则 */}
      <Card
        title={<Space><FilterOutlined />{t(' Custom firewall rules')}</Space>}
        extra={
          <Button type="primary" icon={<PlusOutlined />} size={isMobile ? 'small' : 'middle'} onClick={handleCreateRule}>{t('Add rule')}</Button>
        }
        size={isMobile ? 'small' : 'default'}
        style={{ marginBottom: 24 }}
      >
        <Alert
          message={t('Firewall rules run before page rules. Matching an Allow rule skips subsequent WAF checks. Rules are evaluated in ascending priority order.')}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Table
          dataSource={fwRules}
          columns={isMobile ? fwRuleColumns.filter(c => ['name', 'action', t('Actions')].includes(c.title)) : fwRuleColumns}
          rowKey="id"
          loading={fwLoading}
          size="small"
          pagination={false}
          locale={{ emptyText: <Empty description={t('No firewall rules yet. Click "Add rule" in the top right.')} /> }}
        />
      </Card>

      {/* WAF 预设规则集（现有面板） */}
      <Card
        title={
          <Space>
            <SafetyOutlined />
            {isMobile ? t('WAF rulesets') : t('WAF preset rulesets')}
          </Space>
        }
        extra={
          <Switch
            checked={wafEnabled}
            onChange={handleWafToggle}
            loading={wafToggling}
            checkedChildren={t('Enable')}
            unCheckedChildren={t('Disable')}
          />
        }
        size={isMobile ? 'small' : 'default'}
      >
        <div style={{ marginBottom: 12, fontSize: 12, color: '#888' }}>{t('Disabling WAF only disables preset rulesets and custom WAF rules. The firewall rules above will still apply.')}</div>
        <div style={{ marginBottom: 16 }}>
          <Space wrap size={isMobile ? 'small' : 'middle'}>
            <Tag color="green" icon={<CheckCircleOutlined />}>{t('{{length}} enabled', { length: crsActive ? categoryList.filter((c) => c.on).length : enabledRulesets.length })}</Tag>
            {!crsActive && rulesetsVersion && <Tag color="blue">v{rulesetsVersion}</Tag>}
            {rulesetsDirty && <Tag color="orange">{t('Unsaved changes')}</Tag>}
            <Button type="primary" danger={rulesetsDirty} onClick={handleSaveConfig} loading={loading} disabled={!wafEnabled} size={isMobile ? 'small' : 'middle'}>{t('Save')}</Button>
            <Button onClick={() => { loadWafRulesets(); loadWafCategories(); }} icon={<ReloadOutlined />} disabled={loading} size={isMobile ? 'small' : 'middle'}>
              {isMobile ? t('Refresh') : t('Refresh ruleset')}
            </Button>
          </Space>
        </div>

        {/* 防护强度:一个滑块 = paranoia level + 异常分阈值的预设组合;精确值收在「高级」 */}
        <Card
          size="small"
          type="inner"
          title={<Space><ThunderboltOutlined />{t('Protection level')}</Space>}
          style={{ marginBottom: 16 }}
        >
          {(() => {
            const pl = wafConfig.paranoia_level || 1;
            const th = wafConfig.score_threshold || 5;
            const idx = WAF_STRENGTH_LEVELS.findIndex((l) => l.pl === pl && l.th === th);
            const set = (patch) => { setWafConfig({ ...wafConfig, ...patch }); setRulesetsDirty(true); };
            const cur = idx >= 0 ? WAF_STRENGTH_LEVELS[idx] : null;
            return (
              <>
                <Row gutter={[16, 8]} align="middle">
                  <Col xs={24} md={16}>
                    <Slider
                      min={0}
                      max={WAF_STRENGTH_LEVELS.length - 1}
                      step={null}
                      value={idx >= 0 ? idx : Math.min(pl, WAF_STRENGTH_LEVELS.length - 1)}
                      marks={Object.fromEntries(WAF_STRENGTH_LEVELS.map((l, i) => [i, t(l.label)]))}
                      tooltip={{ open: false }}
                      disabled={!wafEnabled}
                      onChange={(i) => set({ paranoia_level: WAF_STRENGTH_LEVELS[i].pl, score_threshold: WAF_STRENGTH_LEVELS[i].th })}
                    />
                  </Col>
                  <Col xs={24} md={8}>
                    <Segmented
                      block
                      value={wafConfig.mode || 'active'}
                      disabled={!wafEnabled}
                      onChange={(v) => set({ mode: v })}
                      options={[
                        { value: 'active', label: t('Block') },
                        { value: 'simulate', label: t('Log only') },
                        { value: 'passive', label: t('Close') },
                      ]}
                    />
                  </Col>
                </Row>
                <div style={{ marginTop: 4, color: '#666', fontSize: 13 }}>
                  {cur ? t(cur.desc) : t('Custom: PL{{pl}} · threshold {{th}}', { pl, th })}
                </div>
                <Collapse
                  ghost
                  size="small"
                  style={{ marginTop: 4 }}
                  items={[{
                    key: 'adv',
                    label: t('Advanced'),
                    children: (
                      <Row gutter={[16, 12]}>
                        <Col xs={24} sm={12}>
                          <div style={{ marginBottom: 4, fontSize: 13 }}>
                            {t('Paranoia level')}
                            <Tooltip title={t('Higher levels enable more detection rules and more false positives')}>
                              <InfoCircleOutlined style={{ marginLeft: 4, color: '#999' }} />
                            </Tooltip>
                          </div>
                          <Select
                            value={pl}
                            onChange={(v) => set({ paranoia_level: v })}
                            disabled={!wafEnabled}
                            style={{ width: '100%' }}
                            options={[1, 2, 3, 4].map((v) => ({ value: v, label: `PL${v}` }))}
                          />
                        </Col>
                        <Col xs={24} sm={12}>
                          <div style={{ marginBottom: 4, fontSize: 13 }}>
                            {t('Anomaly score threshold')}
                            <Tooltip title={t('Block once matched rules add up to this score; lower is more sensitive')}>
                              <InfoCircleOutlined style={{ marginLeft: 4, color: '#999' }} />
                            </Tooltip>
                          </div>
                          <InputNumber min={1} max={100} value={th} onChange={(v) => set({ score_threshold: v || 5 })} disabled={!wafEnabled} style={{ width: '100%' }} />
                        </Col>
                      </Row>
                    ),
                  }]}
                />
              </>
            );
          })()}
        </Card>

        {crsActive ? renderCategoryGrid() : (
        <>
        {wafCategories && (
          <div style={{ marginBottom: 12, fontSize: 12, color: '#888' }}>
            {t('This domain uses the basic rule sets below. Category switches appear once the OWASP engine is enabled for it.')}
          </div>
        )}
        {Object.keys(rulesetsByCategory).length === 0 ? (
          <Alert message={t('No rulesets available')} description={t('Please try again later or contact an administrator')} type="warning" showIcon />
        ) : (
          <Collapse defaultActiveKey={['injection']} ghost>
            {Object.entries(rulesetsByCategory).map(([category, rulesets]) => (
              <Panel
                header={
                  <Space wrap size={isMobile ? 'small' : 'middle'}>
                    <span style={{ fontSize: isMobile ? '14px' : '18px' }}>{CATEGORY_ICONS[category]}</span>
                    <strong>{t(CATEGORY_NAMES[category])}</strong>
                    <Tag color="blue">
                      {rulesets.filter(r => enabledRulesets.includes(r.key)).length}/{rulesets.length}
                    </Tag>
                  </Space>
                }
                key={category}
              >
                <Row gutter={[12, 12]}>
                  {rulesets.map((ruleset) => renderWafTile({
                    key: ruleset.key, name: t(ruleset.name), desc: t(ruleset.description),
                    count: Array.isArray(ruleset.rules) ? ruleset.rules.filter((r) => r?.enabled !== false).length : 0,
                    extra: <Tag color={SEVERITY_COLORS[ruleset.severity]} style={{ marginInlineEnd: 0, fontSize: 11, lineHeight: '16px' }}>{t(SEVERITY_NAMES[ruleset.severity])}</Tag>,
                    locked: !!allowedRulesets && !allowedRulesets.includes(ruleset.key),
                    on: enabledRulesets.includes(ruleset.key) && (!allowedRulesets || allowedRulesets.includes(ruleset.key)),
                    onToggle: (checked) => handleRulesetToggle(ruleset.key, checked),
                  }))}
                </Row>
              </Panel>
            ))}
          </Collapse>
        )}
        </>
        )}
      </Card>

      {/* 自定义 WAF 评分规则（custom_rules） */}
      {renderCustomRulesCard()}
    </div>
  );

  // ===================== Tab 2: WAF 分析 =====================

  const topRuleColumns = [
    { title: t('Rule ID'), dataIndex: 'fw_rule_id', ellipsis: true },
    { title: t('Rule name'), dataIndex: 'fw_rule_name', ellipsis: true },
    { title: t('Type'), dataIndex: 'fw_rule_type', render: v => <Tag>{v}</Tag> },
    { title: t('Action'), dataIndex: 'fw_action', render: v => <Tag color={ACTION_COLORS[v]}>{t(ACTION_LABELS[v] || v)}</Tag> },
    { title: t('Hits'), dataIndex: 'hits', sorter: (a, b) => a.hits - b.hits },
    { title: t('Dedicated IP'), dataIndex: 'unique_ips' },
  ];

  const topIPColumns = [
    { title: t('IP address'), dataIndex: 'client_ip' },
    { title: t('Country'), dataIndex: 'country_code', render: v => (v ? <span style={{ whiteSpace: 'nowrap' }}><CountryFlag code={v} />{v}</span> : '-') },
    { title: t('Event count'), dataIndex: 'events', sorter: (a, b) => a.events - b.events },
    { title: t('Blocked requests'), dataIndex: 'blocks' },
    { title: t('Rules triggered'), dataIndex: 'rules_triggered' },
  ];

  const renderAnalyticsTab = () => (
    <Spin spinning={analyticsLoading}>
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <RangePicker
            showTime
            value={analyticsRange}
            onChange={(r) => setAnalyticsRange(clampRange(r, retDays))}
            disabledDate={retentionDisabledDate(retDays)}
            size={isMobile ? 'small' : 'middle'}
            presets={[
              { label: t('Last 1 hour'), value: [dayjs().subtract(1, 'hour'), dayjs()], days: 0 },
              { label: t('Last 24 hours'), value: [dayjs().subtract(24, 'hour'), dayjs()], days: 1 },
              { label: t('Last 7 days'), value: [dayjs().subtract(7, 'day'), dayjs()], days: 7 },
              { label: t('Last 30 days'), value: [dayjs().subtract(30, 'day'), dayjs()], days: 30 },
            ].filter((p) => !retDays || p.days <= retDays).map(({ days, ...p }) => p)}
          />
          <RetentionHint days={retDays} />
          <Button icon={<ReloadOutlined />} onClick={loadAnalytics} size={isMobile ? 'small' : 'middle'}>{t('Refresh')}</Button>
        </div>

        <Row gutter={[16, 16]}>
          <Col xs={12} sm={6}>
            <Card size="small"><Statistic title={t('Total events')} value={overview.total_events || 0} prefix={<BarChartOutlined />} /></Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small"><Statistic title={t('Blocked requests')} value={overview.blocked || 0} valueStyle={{ color: '#ff4d4f' }} prefix={<StopOutlined />} /></Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small"><Statistic title={t('Attacking IPs')} value={overview.unique_ips || 0} prefix={<WarningOutlined />} /></Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small"><Statistic title={t('Rules triggered')} value={overview.unique_rules || 0} prefix={<FilterOutlined />} /></Card>
          </Col>
        </Row>

        <Card title={t('Event timeline')} size="small">
          {timeline.length > 0 ? (
            <div ref={timelineChartRef} style={{ width: '100%', height: 300 }} />
          ) : (
            <Empty description={t('No data yet')} />
          )}
        </Card>

        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <Card title={t('Top triggered rules')} size="small">
              <Table
                dataSource={topRules}
                columns={isMobile ? topRuleColumns.filter(c => [t('Rule name'), t('Action'), t('Hits')].includes(c.title)) : topRuleColumns}
                rowKey={(r, i) => i}
                size="small"
                pagination={false}
                scroll={{ x: isMobile ? undefined : 600 }}
              />
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title={t('Top attacking IPs')} size="small">
              <Table
                dataSource={topIPs}
                columns={isMobile ? topIPColumns.filter(c => [t('IP address'), t('Event count'), t('Blocked requests')].includes(c.title)) : topIPColumns}
                rowKey={(r, i) => i}
                size="small"
                pagination={false}
              />
            </Card>
          </Col>
        </Row>
      </Space>
    </Spin>
  );

  // ===================== Tab 3: 事件日志 =====================

  const logColumns = [
    {
      title: t('Time'),
      dataIndex: 'timestamp',
      width: 160,
      render: v => v || '-',
    },
    { title: 'IP', dataIndex: 'client_ip', width: 130 },
    { title: t('Method'), dataIndex: 'method', width: 60 },
    { title: 'URI', dataIndex: 'uri', ellipsis: true },
    {
      title: t('Rules'),
      dataIndex: 'fw_rule_name',
      ellipsis: true,
      render: (v, r) => v || r.fw_rule_id || '-',
    },
    {
      title: t('Action'),
      dataIndex: 'fw_action',
      width: 90,
      render: v => <Tag color={ACTION_COLORS[v]}>{t(ACTION_LABELS[v] || v)}</Tag>,
    },
    { title: t('Country'), dataIndex: 'country_code', width: 80, render: v => (v ? <span style={{ whiteSpace: 'nowrap' }}><CountryFlag code={v} />{v}</span> : '-') },
  ];

  const renderLogsTab = () => (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <RangePicker
          showTime
          value={logsRange}
          onChange={(r) => setLogsRange(clampRange(r, retDays))}
          disabledDate={retentionDisabledDate(retDays)}
          size={isMobile ? 'small' : 'middle'}
          presets={[
            { label: t('Last 1 hour'), value: [dayjs().subtract(1, 'hour'), dayjs()], days: 0 },
            { label: t('Last 24 hours'), value: [dayjs().subtract(24, 'hour'), dayjs()], days: 1 },
            { label: t('Last 7 days'), value: [dayjs().subtract(7, 'day'), dayjs()], days: 7 },
          ].filter((p) => !retDays || p.days <= retDays).map(({ days, ...p }) => p)}
        />
        <Select
          placeholder={t('Action type')}
          allowClear
          style={{ width: 120 }}
          size={isMobile ? 'small' : 'middle'}
          onChange={v => setLogFilters(f => ({ ...f, action: v }))}
          options={Object.entries(ACTION_LABELS).map(([k, v]) => ({ value: k, label: t(v) }))}
        />
        <Input
          placeholder={t('IP address')}
          allowClear
          style={{ width: 140 }}
          size={isMobile ? 'small' : 'middle'}
          onPressEnter={e => setLogFilters(f => ({ ...f, client_ip: e.target.value || undefined }))}
        />
        <Input
          placeholder="URI"
          allowClear
          style={{ width: 160 }}
          size={isMobile ? 'small' : 'middle'}
          onPressEnter={e => setLogFilters(f => ({ ...f, uri: e.target.value || undefined }))}
        />
        <Button icon={<SearchOutlined />} onClick={loadLogs} size={isMobile ? 'small' : 'middle'}>{t('Query')}</Button>
      </div>

      <Table
        dataSource={logs}
        columns={isMobile ? logColumns.filter(c => [t('Time'), 'IP', t('Action')].includes(c.title)) : logColumns}
        rowKey={(r, i) => r.request_id || i}
        loading={logsLoading}
        size="small"
        pagination={{
          current: logsPage,
          pageSize: logsPageSize,
          total: logsTotal,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100', '200'],
          showTotal: (total) => t('{{total}} total', { total }),
          onChange: (page, pageSize) => {
            setLogsPage(page);
            setLogsPageSize(pageSize);
          },
        }}
        scroll={{ x: isMobile ? undefined : 900 }}
        expandable={{
          expandedRowRender: (record) => (
            <div style={{ padding: 8, fontSize: 12 }}>
              <Row gutter={[16, 8]}>
                <Col span={12}><strong>{t('Rule ID:')}</strong> {record.fw_rule_id}</Col>
                <Col span={12}><strong>{t('Rule type:')}</strong> {record.fw_rule_type}</Col>
                <Col span={12}><strong>{t('Message:')}</strong> {record.fw_message}</Col>
                <Col span={12}><strong>{t('Match key:')}</strong> {record.fw_matched_key || '-'}</Col>
                <Col span={24}><strong>{t('Match value:')}</strong> {record.fw_matched_value || '-'}</Col>
                <Col span={24}><strong>User-Agent:</strong> {record.http_user_agent || '-'}</Col>
                <Col span={12}><strong>Host:</strong> {record.host}</Col>
                <Col span={12}><strong>{t('Status code:')}</strong> {record.status}</Col>
              </Row>
            </div>
          ),
        }}
      />
    </Space>
  );

  // ===================== 规则编辑 Modal =====================

  const selectedAction = Form.useWatch('action', fwForm);
  // 限速富参：按维度/计量联动表单。
  const rlKeyBy = Form.useWatch(['action_config', 'key_by'], fwForm);
  const rlMetric = Form.useWatch(['action_config', 'metric'], fwForm);

  const renderRuleModal = () => (
      <Modal
        title={editingRule ? t('Edit firewall rule') : t('Add firewall rule')}
        open={fwModalVisible}
        onOk={handleSaveRule}
        onCancel={() => setFwModalVisible(false)}
        width={isMobile ? '95%' : 720}
        destroyOnClose
      >
        <Form form={fwForm} layout="vertical">
          <Row gutter={16}>
            <Col span={16}>
              <Form.Item name="name" label={t('Rule name')} rules={[{ required: true, message: t('Enter a rule name') }]}>
                <Input placeholder={t('E.g. block malicious IPs')} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="priority" label={t('Priority')}>
                <InputNumber min={1} max={9999} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="description" label={t('Description')}>
            <Input placeholder={t('Optional description')} />
          </Form.Item>

          <Divider orientation="left" style={{ margin: '12px 0' }}>{t('Match conditions')}</Divider>

          {/* 条件只在点「确定」时校验,错误显示在编辑器下方一行(不把整个编辑器标红) */}
          <Form.Item name="expression" style={{ marginBottom: fwCondError ? 4 : 24 }}>
            <RuleConditionEditor onEdit={() => fwCondError && setFwCondError(null)} />
          </Form.Item>
          {fwCondError && <div style={{ color: 'var(--ant-color-error, #ff4d4f)', marginBottom: 16 }}>{fwCondError}</div>}

          <Divider orientation="left" style={{ margin: '12px 0' }}>{t('Action')}</Divider>

          <Form.Item name="action" label={t('Action type')} rules={[{ required: true }]}>
            <Select options={Object.entries(ACTION_LABELS).map(([k, v]) => ({ value: k, label: t(v) }))} />
          </Form.Item>

          {selectedAction === 'block' && (
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name={['action_config', 'status']} label={t('Status code')} initialValue={403}>
                  <InputNumber min={400} max={599} />
                </Form.Item>
              </Col>
              <Col span={16}>
                <Form.Item name={['action_config', 'message']} label={t('Message')}>
                  <Input placeholder="Access Denied" />
                </Form.Item>
              </Col>
            </Row>
          )}

          {selectedAction === 'ratelimit' && (
            <>
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
                message={t('Advanced rate limiting: count by the selected dimension within a sliding window and apply the over-limit action when the limit is reached.')}
              />
              {rlMetric !== 'bandwidth' && (
                <Form.Item noStyle shouldUpdate>
                  {({ getFieldValue }) => (
                    <div style={{ marginBottom: 12 }}>
                      <RateLimitPresets
                        requests={getFieldValue(['action_config', 'requests'])}
                        ttl={getFieldValue(['action_config', 'ttl'])}
                        onPick={(p) => fwForm.setFieldsValue({ action_config: { requests: p.requests, ttl: p.ttl } })}
                      />
                    </div>
                  )}
                </Form.Item>
              )}
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name={['action_config', 'requests']}
                    label={rlMetric === 'bandwidth' ? t('Bandwidth limit (per second)') : t('Max requests')}
                    initialValue={100}
                    rules={[{ required: true, message: t('Please enter an upper limit') }]}
                  >
                    {rlMetric === 'bandwidth'
                      ? <SizeInput presets={[131072, 524288, 1048576, 5242880, 10485760]} min={1024} max={10737418240} suffix="/s" />
                      : <CountInput presets={[10, 30, 60, 100, 300, 1000]} min={1} max={1000000} />}
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name={['action_config', 'ttl']} label={t('Per')} initialValue={60} rules={[{ required: true, message: t('Enter a window size') }]}>
                    <DurationInput presets={[1, 10, 60, 300, 3600]} min={1} max={86400} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name={['action_config', 'metric']} label={t('Metering method')} initialValue="requests" tooltip={t('requests=count requests; bandwidth=count response bytes (protect against high-volume downloads)')}>
                    <Select options={[
                      { value: 'requests', label: t('Request count') },
                      { value: 'bandwidth', label: t('Bandwidth (bytes)') },
                    ]} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={rlKeyBy === 'header' || rlKeyBy === 'cookie' ? 8 : 12}>
                  <Form.Item name={['action_config', 'key_by']} label={t('Rate limit dimension')} initialValue="ip" tooltip={t('Dimension to group counts by')}>
                    <Select options={[
                      { value: 'ip', label: t('IP address') },
                      { value: 'header', label: t('Request headers') },
                      { value: 'cookie', label: 'Cookie' },
                      { value: 'uri', label: t('URI path') },
                      { value: 'country', label: t('Country') },
                    ]} />
                  </Form.Item>
                </Col>
                {(rlKeyBy === 'header' || rlKeyBy === 'cookie') && (
                  <Col span={8}>
                    <Form.Item
                      name={['action_config', 'key_name']}
                      label={rlKeyBy === 'header' ? t('Request header name') : t('Cookie name')}
                      rules={[{ required: true, message: t('Please enter a name') }]}
                    >
                      <Input placeholder={rlKeyBy === 'header' ? t('e.g. X-Api-Key') : t('e.g. session_id')} />
                    </Form.Item>
                  </Col>
                )}
                <Col span={rlKeyBy === 'header' || rlKeyBy === 'cookie' ? 8 : 12}>
                  <Form.Item name={['action_config', 'scope']} label={t('Scope')} initialValue="domain" tooltip={t('Per key: each IP (or the chosen key) is counted separately; Per key + path: also split by URI path; Total: all requests for this rule share one counter')}>
                    <Select options={[
                      { value: 'domain', label: t('Per key') },
                      { value: 'path', label: t('Per key + path') },
                      { value: 'global', label: t('Total') },
                    ]} />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name={['action_config', 'on_exceed']} label={t('Over-limit action')} initialValue="block" tooltip={t('Action for subsequent requests when the limit is reached')}>
                <Select options={[
                  { value: 'block', label: t('Block (429)') },
                  { value: 'challenge', label: t('Human challenge') },
                  { value: 'log', label: t('Log only') },
                  { value: 'tarpit', label: t('Delay (tarpit)') },
                ]} />
              </Form.Item>
            </>
          )}

          {(selectedAction === 'js_challenge' || selectedAction === 'managed_challenge') && (
            <Form.Item label={t('Validity after passing')}
              extra={selectedAction === 'managed_challenge' ? t('Uses the captcha.la keys from Access control → UAM; the platform CAPTCHA is used if not set') : undefined}>
              <span>{t('Taken from Access control → UAM → Pass validity')}</span>
            </Form.Item>
          )}
        </Form>
      </Modal>
  );

  // ===================== Tab: 访问控制（专用安全区） =====================
  // 专用防盗链(referers) / 地区封禁(geo) / UA 名单(uafilter) + 访问鉴权(auth)。
  // 存入 domains.settings.security，下发映射到 Rust referers/geo/ua_filter/auth 专用模块，
  // 与「自定义防火墙规则」表分离（收敛），避免与通用表达式规则冲突。

  const UA_MATCH_TYPES = [
    { value: 'keyword', label: t('Contains (substring)') },
    { value: 'wildcard', label: t('Wildcard (* ?)') },
    { value: 'regex', label: t('Regex') },
    { value: 'exact', label: t('Exact') },
    { value: 'prefix', label: t('Prefix') },
    { value: 'suffix', label: t('Suffix') },
  ];

  const linesJoin = (arr) => (Array.isArray(arr) ? arr.join('\n') : (arr || ''));

  const securityToForm = (sec) => {
    sec = sec || {};
    const h = sec.hotlink || {};
    const g = sec.geo || {};
    const u = sec.ua_filter || {};
    const a = sec.auth || {};
    return {
      hotlink: {
        enabled: !!h.enabled,
        allow_empty: !!h.allow_empty,
        allow_same_domain: !!h.allow_same_domain,
        allow_domains: linesJoin(h.allow_domains),
        deny_domains: linesJoin(h.deny_domains),
        extensions: linesJoin(h.extensions),
      },
      geo: {
        enabled: !!g.enabled,
        deny_countries: linesJoin(g.deny_countries),
        allow_countries: linesJoin(g.allow_countries),
        deny_regions: linesJoin(g.deny_regions),
        allow_regions: linesJoin(g.allow_regions),
        deny_asns: linesJoin(g.deny_asns),
        allow_asns: linesJoin(g.allow_asns),
      },
      ua_filter: {
        enabled: !!u.enabled,
        default_action: u.default_action || 'allow',
        empty_action: u.empty_action || '',
        deny_status: u.deny_status || 0,
        case_sensitive: !!u.case_sensitive,
        rules: (u.rules || []).map((r) => ({
          action: r.action || 'deny',
          match_type: r.match_type || 'keyword',
          pattern: r.pattern || '',
        })),
      },
      auth: {
        enabled: !!a.enabled,
        combine: a.combine || 'and',
        ip_allow: linesJoin(a.ip_allow),
        ip_deny: linesJoin(a.ip_deny),
        methods: (a.methods || []).map((m) => {
          if (m.type === 'basic') {
            return {
              type: 'basic',
              realm: m.realm || '',
              users_text: (m.users || []).map((x) => `${x.username || ''}:${x.password || ''}`).join('\n'),
            };
          }
          if (m.type === 'token') {
            return { type: 'token', param: m.param || 'token', location: m.location || 'query', tokens: linesJoin(m.tokens) };
          }
          if (m.type === 'sign_url') {
            return {
              type: 'sign_url',
              sign_type: m.sign_type || 'a',
              key: m.key || '',
              param: m.param || '',
              valid_window: m.valid_window || 0,
              skew: m.skew || 0,
            };
          }
          return { type: m.type };
        }),
      },
      uam: {
        mode: (sec.uam || {}).mode || 'off',
        min_wait: (sec.uam || {}).min_wait ?? 5,
        difficulty: (sec.uam || {}).difficulty ?? 15,
        clearance_ttl: (sec.uam || {}).clearance_ttl ?? 1800,
        captcha: {
          provider: ((sec.uam || {}).captcha || {}).provider || 'captchala',
          site_key: ((sec.uam || {}).captcha || {}).site_key || '',
          secret: ((sec.uam || {}).captcha || {}).secret || '',
          verify_url: ((sec.uam || {}).captcha || {}).verify_url || '',
        },
        whitelist: {
          ips: linesJoin(((sec.uam || {}).whitelist || {}).ips),
          static_exts: linesJoin(((sec.uam || {}).whitelist || {}).static_exts),
          bot_asns: linesJoin(((sec.uam || {}).whitelist || {}).bot_asns),
        },
      },
      protect: {
        enabled: !!(sec.protect || {}).enabled,
        max_conns_per_ip: (sec.protect || {}).max_conns_per_ip ?? 0,
        slowloris: {
          enabled: !!((sec.protect || {}).slowloris || {}).enabled,
          min_body_rate: ((sec.protect || {}).slowloris || {}).min_body_rate ?? 0,
          window_secs: ((sec.protect || {}).slowloris || {}).window_secs ?? 0,
          grace_secs: ((sec.protect || {}).slowloris || {}).grace_secs ?? 0,
          max_idle_secs: ((sec.protect || {}).slowloris || {}).max_idle_secs ?? 0,
          max_body_time: ((sec.protect || {}).slowloris || {}).max_body_time ?? 0,
        },
      },
    };
  };

  const buildSecurityPayload = (v) => {
    v = v || {};
    const h = v.hotlink || {};
    const g = v.geo || {};
    const u = v.ua_filter || {};
    const a = v.auth || {};
    return {
      hotlink: {
        enabled: !!h.enabled,
        allow_empty: !!h.allow_empty,
        allow_same_domain: !!h.allow_same_domain,
        allow_domains: h.allow_domains || '',
        deny_domains: h.deny_domains || '',
        extensions: h.extensions || '',
      },
      geo: {
        enabled: !!g.enabled,
        deny_countries: g.deny_countries || '',
        allow_countries: g.allow_countries || '',
        deny_regions: g.deny_regions || '',
        allow_regions: g.allow_regions || '',
        deny_asns: g.deny_asns || '',
        allow_asns: g.allow_asns || '',
      },
      ua_filter: {
        enabled: !!u.enabled,
        default_action: u.default_action || 'allow',
        empty_action: u.empty_action || '',
        deny_status: u.deny_status || 0,
        case_sensitive: !!u.case_sensitive,
        rules: (u.rules || [])
          .filter((r) => r && (r.pattern || '').trim())
          .map((r) => ({ action: r.action || 'deny', match_type: r.match_type || 'keyword', pattern: r.pattern })),
      },
      auth: {
        enabled: !!a.enabled,
        combine: a.combine || 'and',
        ip_allow: a.ip_allow || '',
        ip_deny: a.ip_deny || '',
        methods: (a.methods || [])
          .map((m) => {
            if (!m || !m.type) return null;
            if (m.type === 'basic') {
              const users = (m.users_text || '')
                .split('\n')
                .map((l) => l.trim())
                .filter(Boolean)
                .map((l) => {
                  const idx = l.indexOf(':');
                  return idx >= 0
                    ? { username: l.slice(0, idx), password: l.slice(idx + 1) }
                    : { username: l, password: '' };
                });
              return { type: 'basic', users, realm: m.realm || '' };
            }
            if (m.type === 'token') {
              return { type: 'token', param: m.param || 'token', location: m.location || 'query', tokens: m.tokens || '' };
            }
            if (m.type === 'sign_url') {
              return {
                type: 'sign_url',
                sign_type: m.sign_type || 'a',
                key: m.key || '',
                param: m.param || '',
                valid_window: m.valid_window || 0,
                skew: m.skew || 0,
              };
            }
            return null;
          })
          .filter(Boolean),
      },
      uam: (() => {
        const um = v.uam || {};
        const wl = um.whitelist || {};
        const toArr = (s) => (s || '').split('\n').map((x) => x.trim()).filter(Boolean);
        return {
          mode: um.mode || 'off',
          min_wait: um.min_wait || 0,
          difficulty: um.difficulty || 0,
          clearance_ttl: um.clearance_ttl || 0,
          captcha: {
            provider: (um.captcha || {}).provider || 'captchala',
            site_key: (um.captcha || {}).site_key || '',
            secret: (um.captcha || {}).secret || '',
            verify_url: (um.captcha || {}).verify_url || '',
          },
          whitelist: {
            ips: toArr(wl.ips),
            static_exts: toArr(wl.static_exts),
            bot_asns: toArr(wl.bot_asns).map((n) => parseInt(n, 10)).filter((n) => !Number.isNaN(n)),
          },
        };
      })(),
      protect: (() => {
        const p = v.protect || {};
        const sl = p.slowloris || {};
        return {
          enabled: !!p.enabled,
          max_conns_per_ip: p.max_conns_per_ip || 0,
          slowloris: {
            enabled: !!sl.enabled,
            min_body_rate: Number(sl.min_body_rate) || 0,
            window_secs: sl.window_secs || 0,
            grace_secs: sl.grace_secs || 0,
            max_idle_secs: sl.max_idle_secs || 0,
            max_body_time: sl.max_body_time || 0,
          },
        };
      })(),
    };
  };

  const handleSaveSecurity = async () => {
    setSecSaving(true);
    try {
      await secForm.validateFields();
      // 用 getFieldsValue(true) 取完整 store（含条件渲染未挂载、但已由 setFieldsValue 预填的字段，
      // 如 mode!==captcha 时的 captcha.site_key/secret），避免保存把已存验证码密钥清空（#12）。
      const security = buildSecurityPayload(secForm.getFieldsValue(true));
      const response = await cdnService.updateDomain({ id: domainData.id, security });
      if (response.success) {
        message.success(t('Access control configuration saved'));
        reloadDomain && reloadDomain();
      } else {
        message.error(response.error || t('Failed to save'));
      }
    } catch (error) {
      if (error && error.errorFields) return;
      message.error(t('Failed to save'));
    } finally {
      setSecSaving(false);
    }
  };

  const renderSecurityTab = () => (
    <Form form={secForm} layout="vertical" size={isMobile ? 'small' : 'middle'}>
      <Alert
        message={t('Access control is a dedicated security module for this domain, independent of the Custom firewall rules above. Once enabled, its features take effect at the edge according to their individual switches.')}
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      {/* 专用防盗链 → Rust referers */}
      <Card
        title={<Space><StopOutlined />{t(' Hotlink protection (Referer list)')}</Space>}
        size={isMobile ? 'small' : 'default'}
        style={{ marginBottom: 16 }}
        extra={<Form.Item name={['hotlink', 'enabled']} valuePropName="checked" noStyle><Switch checkedChildren={t('Enable')} unCheckedChildren={t('Disable')} /></Form.Item>}
      >
        <Row gutter={16}>
          <Col xs={12} sm={8}>
            <Form.Item name={['hotlink', 'allow_empty']} label={t('Allow empty Referer')} valuePropName="checked" tooltip={t('Direct visits / private browsing often have an empty Referer')}><Switch /></Form.Item>
          </Col>
          <Col xs={12} sm={8}>
            <Form.Item name={['hotlink', 'allow_same_domain']} label={t('Allow same domain')} valuePropName="checked" tooltip={t('Allow when the Referer host matches the site\'s domain or a subdomain')}><Switch /></Form.Item>
          </Col>
        </Row>
        <Form.Item name={['hotlink', 'allow_domains']} label={t('Allowlisted domains')} tooltip={t('One per line. Supports exact domains, wildcards (*.example.com / img.*), and regular expressions (prefixed with ~). If provided, the Referer must match one entry.')}>
          <TagsInput format="lines" commaSeparates={false} spaceSeparates={false} placeholder="example.com  *.example.com  ~^https?://cdn\.trusted\.com/" />
        </Form.Item>
        <Form.Item name={['hotlink', 'deny_domains']} label={t('Blacklisted domains')} tooltip={t('One per line. Matches are blocked (takes priority over the allowlist).')}>
          <TagsInput format="lines" commaSeparates={false} spaceSeparates={false} placeholder="thief.com  *.badsite.net" />
        </Form.Item>
        <Form.Item name={['hotlink', 'extensions']} label={t('Defensive TLDs only')} tooltip={t('Leave blank to apply to all paths. Enter extensions (one per line, e.g., .jpg .mp4) to apply hotlink protection only to these resources.')}>
          <TagsInput format="lines" lowercase placeholder=".jpg, .png, .mp4" />
        </Form.Item>
      </Card>

      {/* 专用地区封禁 → Rust geo */}
      <Card
        title={<Space><FilterOutlined />{t(' Region / ASN blocking')}</Space>}
        size={isMobile ? 'small' : 'default'}
        style={{ marginBottom: 16 }}
        extra={<Form.Item name={['geo', 'enabled']} valuePropName="checked" noStyle><Switch checkedChildren={t('Enable')} unCheckedChildren={t('Disable')} /></Form.Item>}
      >
        <Alert message={t('Blacklist matches are blocked. If the whitelist is not empty, only listed entries (resolved IPs) are allowed. Use ISO alpha-2 codes for countries (CN/US), ISO subdivision codes for provinces (e.g., ENG), and numbers for ASNs. Conditions across dimensions are combined with AND.')} type="warning" showIcon style={{ marginBottom: 12 }} />
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item name={['geo', 'deny_countries']} label={t('Country blocklist')} extra={<CountryFlagList value={denyCountriesWatch} />}><TagsInput format="lines" uppercase placeholder="CN RU" validate={(v) => (isCountryCode(v) ? null : t('Use a 2-letter country code, e.g. US'))} /></Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name={['geo', 'allow_countries']} label={t('Country allowlist')} extra={<CountryFlagList value={allowCountriesWatch} />}><TagsInput format="lines" uppercase placeholder="US GB" validate={(v) => (isCountryCode(v) ? null : t('Use a 2-letter country code, e.g. US'))} /></Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name={['geo', 'deny_regions']} label={t('Province/state blocklist')}><TagsInput format="lines" uppercase placeholder="ENG" validate={(v) => (isRegionCode(v) ? null : t('Invalid region code'))} /></Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name={['geo', 'allow_regions']} label={t('Province/state allowlist')}><TagsInput format="lines" uppercase validate={(v) => (isRegionCode(v) ? null : t('Invalid region code'))} /></Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name={['geo', 'deny_asns']} label={t('ASN blocklist')}><TagsInput format="lines" placeholder="15169 13335" validate={(v) => (isAsn(v) ? null : t('ASN must be a number'))} /></Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name={['geo', 'allow_asns']} label={t('ASN allowlist')}><TagsInput format="lines" validate={(v) => (isAsn(v) ? null : t('ASN must be a number'))} /></Form.Item>
          </Col>
        </Row>
      </Card>

      {/* 专用 UA 名单 → Rust ua_filter */}
      <Card
        title={<Space><SafetyOutlined />{t(' User-Agent list')}</Space>}
        size={isMobile ? 'small' : 'default'}
        style={{ marginBottom: 16 }}
        extra={<Form.Item name={['ua_filter', 'enabled']} valuePropName="checked" noStyle><Switch checkedChildren={t('Enable')} unCheckedChildren={t('Disable')} /></Form.Item>}
      >
        <Row gutter={16}>
          <Col xs={12} sm={6}>
            <Form.Item name={['ua_filter', 'default_action']} label={t('Default action')} tooltip={t('When no rule matches: allow=blocklist mode; deny=allowlist mode')}>
              <Select options={[{ value: 'allow', label: t('Allow') }, { value: 'deny', label: t('Block') }]} />
            </Form.Item>
          </Col>
          <Col xs={12} sm={6}>
            <Form.Item name={['ua_filter', 'empty_action']} label={t('Empty UA')} tooltip={t('Prevent bypass by removing the UA header')}>
              <Select options={[{ value: '', label: t('Follow rules') }, { value: 'allow', label: t('Allow') }, { value: 'deny', label: t('Block') }]} />
            </Form.Item>
          </Col>
          <Col xs={12} sm={6}>
            <Form.Item name={['ua_filter', 'deny_status']} label={t('Block status code')} tooltip={t('0=default 403')}>
              <InputNumber min={0} max={599} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={12} sm={6}>
            <Form.Item name={['ua_filter', 'case_sensitive']} label={t('Case-sensitive')} valuePropName="checked"><Switch /></Form.Item>
          </Col>
        </Row>
        <Divider orientation="left" style={{ margin: '8px 0' }}>{t('Rules (top to bottom; first match determines the action)')}</Divider>
        <Form.List name={['ua_filter', 'rules']}>
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <Row key={key} gutter={8} align="top" style={{ marginBottom: 8 }}>
                  <Col span={5}>
                    <Form.Item {...restField} name={[name, 'action']} rules={[{ required: true }]}>
                      <Select size="small" options={[{ value: 'deny', label: t('Block') }, { value: 'allow', label: t('Allow') }]} />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item {...restField} name={[name, 'match_type']} rules={[{ required: true }]}>
                      <Select size="small" options={UA_MATCH_TYPES} />
                    </Form.Item>
                  </Col>
                  <Col span={11}>
                    <Form.Item {...restField} name={[name, 'pattern']} rules={[{ required: true, message: t('Enter a pattern') }]}>
                      <Input size="small" placeholder={t('e.g. sqlmap / *bot* / ^curl/')} />
                    </Form.Item>
                  </Col>
                  <Col span={2}>
                    <Button type="link" danger size="small" icon={<DeleteOutlined />} onClick={() => remove(name)} />
                  </Col>
                </Row>
              ))}
              <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={() => add({ action: 'deny', match_type: 'keyword', pattern: '' })}>{t('Add UA rule')}</Button>
            </>
          )}
        </Form.List>
      </Card>

      {/* 访问鉴权 → Rust auth */}
      <Card
        title={<Space><CheckCircleOutlined />{t(' Access authentication')}</Space>}
        size={isMobile ? 'small' : 'default'}
        style={{ marginBottom: 16 }}
        extra={<Form.Item name={['auth', 'enabled']} valuePropName="checked" noStyle><Switch checkedChildren={t('Enable')} unCheckedChildren={t('Disable')} /></Form.Item>}
      >
        <Row gutter={16}>
          <Col xs={24} sm={8}>
            <Form.Item name={['auth', 'combine']} label={t('Multiple methods')} tooltip={t('and=all must pass; or=any may pass')}>
              <Select options={[{ value: 'and', label: t('Pass all (AND)') }, { value: 'or', label: t('Pass any (OR)') }]} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item name={['auth', 'ip_allow']} label={t('IP allowlist')} tooltip={t('One exact IP or CIDR per line. If provided, only listed addresses are allowed.')}><TagsInput format="lines" placeholder="10.0.0.0/8, 1.2.3.4" validate={(v) => (isIpOrCidr(v) ? null : t('Invalid IP / CIDR'))} /></Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item name={['auth', 'ip_deny']} label={t('IP blocklist')} tooltip={t('Return 403 on match (takes priority over the allowlist)')}><TagsInput format="lines" placeholder="1.2.3.4, 10.0.0.0/8" validate={(v) => (isIpOrCidr(v) ? null : t('Invalid IP / CIDR'))} /></Form.Item>
          </Col>
        </Row>
        <Divider orientation="left" style={{ margin: '8px 0' }}>{t('Authentication method')}</Divider>
        <Form.List name={['auth', 'methods']}>
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => {
                const mtype = authMethodsWatch?.[name]?.type;
                return (
                  <Card key={key} size="small" style={{ marginBottom: 8 }} bodyStyle={{ paddingBottom: 0 }}>
                    <Row gutter={8} align="middle" style={{ marginBottom: 8 }}>
                      <Col flex="auto">
                        <Form.Item {...restField} name={[name, 'type']} label={t('Type')} rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                          <Select
                            options={[
                              { value: 'basic', label: 'HTTP Basic' },
                              { value: 'token', label: t('Parameter Token') },
                              { value: 'sign_url', label: t('Signed URL (A/B/C/D)') },
                            ]}
                            placeholder={t('Select an authentication method')}
                          />
                        </Form.Item>
                      </Col>
                      <Col>
                        <Button type="link" danger icon={<DeleteOutlined />} onClick={() => remove(name)} />
                      </Col>
                    </Row>

                    {mtype === 'basic' && (
                      <>
                        <Form.Item {...restField} name={[name, 'realm']} label="Realm"><Input placeholder="Restricted" /></Form.Item>
                        <Form.Item {...restField} name={[name, 'users_text']} label={t('Users (one user:pass per line)')} rules={[{ required: true, message: t('At least one user') }]}>
                          <TextArea rows={2} placeholder={'alice:s3cret\nbob:passw0rd'} />
                        </Form.Item>
                      </>
                    )}

                    {mtype === 'token' && (
                      <Row gutter={8}>
                        <Col xs={12} sm={6}>
                          <Form.Item {...restField} name={[name, 'param']} label={t('Parameter name')}><Input placeholder="token" /></Form.Item>
                        </Col>
                        <Col xs={12} sm={6}>
                          <Form.Item {...restField} name={[name, 'location']} label={t('Location')}>
                            <Select options={[{ value: 'query', label: 'Query' }, { value: 'header', label: 'Header' }]} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item {...restField} name={[name, 'tokens']} label={t('Allowed Tokens')} rules={[{ required: true, message: t('At least one token') }]}>
                            <TagsInput format="lines" />
                          </Form.Item>
                        </Col>
                      </Row>
                    )}

                    {mtype === 'sign_url' && (
                      <>
                        <Row gutter={8}>
                          <Col xs={12} sm={6}>
                            <Form.Item {...restField} name={[name, 'sign_type']} label={t('Signature type')}>
                              <Select options={[
                                { value: 'a', label: 'A (query md5)' },
                                { value: 'b', label: 'B (path md5)' },
                                { value: 'c', label: 'C (path md5)' },
                                { value: 'd', label: 'D (HMAC-SHA256)' },
                              ]} />
                            </Form.Item>
                          </Col>
                          <Col xs={12} sm={6}>
                            <Form.Item {...restField} name={[name, 'param']} label={t('Parameter name')} tooltip={t('A defaults to auth_key; D defaults to sign')}><Input placeholder="auth_key / sign" /></Form.Item>
                          </Col>
                          <Col xs={12} sm={6}>
                            <Form.Item {...restField} name={[name, 'valid_window']} label={t('Link validity')} tooltip={t('Types B/C only')}><DurationInput presets={[300, 1800, 3600, 21600, 86400]} zeroLabel={t('Default (30 min)')} min={0} max={2592000} /></Form.Item>
                          </Col>
                          <Col xs={12} sm={6}>
                            <Form.Item {...restField} name={[name, 'skew']} label={t('Clock skew allowed')}><DurationInput presets={[30, 60, 300]} zeroLabel={t('None')} min={0} max={3600} /></Form.Item>
                          </Col>
                        </Row>
                        <Form.Item {...restField} name={[name, 'key']} label={t('Private key / Key')} rules={[{ required: true, message: t('Signing key is required') }]}>
                          <Input.Password placeholder={t('Signing private key (deny all if blank)')} />
                        </Form.Item>
                      </>
                    )}
                  </Card>
                );
              })}
              <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({ type: 'basic', location: 'query', sign_type: 'a' })}>{t('Add authentication method')}</Button>
            </>
          )}
        </Form.List>
      </Card>

      {/* 5秒盾 / 攻击模式 → Rust uam */}
      <Card
        title={<Space><ThunderboltOutlined />{t(' 5-second challenge / Under Attack Mode (UAM)')}</Space>}
        size={isMobile ? 'small' : 'default'}
        style={{ marginBottom: 16 }}
      >
        <Alert message={t('Challenge suspicious traffic to verify visitors are human. Adaptive: automatically challenge based on load. Under attack: challenge all visitors first (5-second shield). Force CAPTCHA: use a third-party CAPTCHA. Visitors who pass receive a pass that skips verification until it expires.')} type="warning" showIcon style={{ marginBottom: 12 }} />
        <Row gutter={16}>
          <Col xs={24} sm={6}>
            <Form.Item name={['uam', 'mode']} label={t('Mode')} tooltip={t('off=disabled; adaptive=challenge automatically under CC pressure (requires connection protection); under_attack=5-second challenge for everyone; captcha=mandatory CAPTCHA')}>
              <Select options={[
                { value: 'off', label: t('Close') },
                { value: 'adaptive', label: t('Adaptive') },
                { value: 'under_attack', label: t('Under attack (5-second challenge)') },
                { value: 'captcha', label: t('Force CAPTCHA') },
              ]} />
            </Form.Item>
          </Col>
          <Col xs={12} sm={6}>
            <Form.Item name={['uam', 'min_wait']} label={t('Minimum wait')} tooltip={t('Below 5 seconds offers little protection')}>
              <DurationInput presets={[3, 5, 8, 10]} zeroLabel={t('Default (5 s)')} min={0} max={60} />
            </Form.Item>
          </Col>
          <Col xs={12} sm={6}>
            <Form.Item name={['uam', 'difficulty']} label={t('Difficulty')} tooltip={t('Proof-of-work difficulty; 15 or higher recommended')}>
              <CountInput presets={[15, 18, 20, 22]} zeroLabel={t('Default (15)')} min={0} max={32} />
            </Form.Item>
          </Col>
          <Col xs={12} sm={6}>
            <Form.Item name={['uam', 'clearance_ttl']} label={t('Pass validity')} tooltip={t('Time after successful verification before another challenge is required')}>
              <DurationInput presets={[900, 1800, 3600, 21600, 86400, 604800]} zeroLabel={t('Default (30 min)')} min={0} max={2592000} />
            </Form.Item>
          </Col>
        </Row>
        {(uamModeWatch === 'captcha' || fwRules.some((r) => r.action === 'managed_challenge')) && (
          <>
            <Divider orientation="left" style={{ margin: '8px 0' }}>{t('CAPTCHA')}</Divider>
            <Row gutter={16}>
              <Col xs={24} sm={6}>
                <Form.Item name={['uam', 'captcha', 'provider']} label={t('Provider')} initialValue="captchala"
                  extra={t('The edge currently supports only captcha.la hosted verification')}>
                  <Select options={[
                    { value: 'captchala', label: t('captcha.la (managed verification)') },
                  ]} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={6}>
                <Form.Item name={['uam', 'captcha', 'site_key']} label="Site Key"><Input placeholder={t('Leave blank to use the platform CAPTCHA')} /></Form.Item>
              </Col>
              <Col xs={24} sm={6}>
                <Form.Item name={['uam', 'captcha', 'secret']} label="Secret"><Input.Password placeholder={domainData?.settings?.security?.uam?.captcha?.secret_set ? t('Set; leave blank to keep it') : t('Server-side key')} /></Form.Item>
              </Col>
              <Col xs={24} sm={6}>
                <Form.Item name={['uam', 'captcha', 'verify_url']} label={t('Validation URL')} tooltip={t('Leave blank to use the provider\'s default verification endpoint')}><Input placeholder="https://..." /></Form.Item>
              </Col>
            </Row>
          </>
        )}
        <Divider orientation="left" style={{ margin: '8px 0' }}>{t('Allowlist (skip challenge)')}</Divider>
        <Row gutter={16}>
          <Col xs={24} sm={8}>
            <Form.Item name={['uam', 'whitelist', 'ips']} label={t('IP / CIDR allowlist')} tooltip={t('Matches are allowed without a challenge')}><TagsInput format="lines" placeholder="10.0.0.0/8, 1.2.3.4" validate={(v) => (isIpOrCidr(v) ? null : t('Invalid IP / CIDR'))} /></Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item name={['uam', 'whitelist', 'static_exts']} label={t('Static resource extensions')} tooltip={t('Requests with these extensions skip the challenge (not applied in Under attack / Force CAPTCHA mode)')}><TagsInput format="lines" lowercase placeholder=".jpg, .css, .js" /></Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item name={['uam', 'whitelist', 'bot_asns']} label={t('Allow bot ASNs')} tooltip={t('Trusted ASNs such as search engines (numbers; not applied in Under attack / Force CAPTCHA mode)')}><TagsInput format="lines" placeholder="15169, 8075" validate={(v) => (/^\d+$/.test(v) ? null : t('ASN must be a number'))} /></Form.Item>
          </Col>
        </Row>
      </Card>

      {/* 连接防护 → Rust protect */}
      <Card
        title={<Space><SafetyOutlined />{t(' Connection protection (Slowloris)')}</Space>}
        size={isMobile ? 'small' : 'default'}
        style={{ marginBottom: 16 }}
        extra={<Form.Item name={['protect', 'enabled']} valuePropName="checked" noStyle><Switch checkedChildren={t('Enable')} unCheckedChildren={t('Disable')} /></Form.Item>}
      >
        <Alert message={t('Limit concurrent connections per IP and detect slow requests (Slowloris) to mitigate connection exhaustion attacks.')} type="warning" showIcon style={{ marginBottom: 12 }} />
        <Row gutter={16}>
          <Col xs={12} sm={8}>
            <Form.Item name={['protect', 'max_conns_per_ip']} label={t('Max concurrent connections per IP')} ><CountInput presets={[20, 50, 100, 200, 500]} zeroLabel={t('Unlimited')} min={0} max={100000} /></Form.Item>
          </Col>
        </Row>
        <Divider orientation="left" style={{ margin: '8px 0' }}>{t('Slowloris protection')}</Divider>
        <Row gutter={16}>
          <Col xs={12} sm={4}>
            <Form.Item name={['protect', 'slowloris', 'enabled']} label={t('Enable')} valuePropName="checked"><Switch /></Form.Item>
          </Col>
          <Col xs={12} sm={4}>
            <Form.Item name={['protect', 'slowloris', 'min_body_rate']} label={t('Minimum upload speed')} tooltip={t('A request body transfer rate below this value is considered a slow attack')}><SizeInput presets={[64, 256, 1024, 10240]} zeroLabel={t('Off')} suffix="/s" min={0} max={104857600} /></Form.Item>
          </Col>
          <Col xs={12} sm={4}>
            <Form.Item name={['protect', 'slowloris', 'window_secs']} label={t('Sampling window')}><DurationInput presets={[0, 5, 10, 30, 60]} min={0} max={600} /></Form.Item>
          </Col>
          <Col xs={12} sm={4}>
            <Form.Item name={['protect', 'slowloris', 'grace_secs']} label={t('Grace period')} tooltip={t('Initial grace period for slow traffic after a connection is established')}><DurationInput presets={[0, 5, 10, 30]} min={0} max={600} /></Form.Item>
          </Col>
          <Col xs={12} sm={4}>
            <Form.Item name={['protect', 'slowloris', 'max_idle_secs']} label={t('Maximum idle time')}><DurationInput presets={[10, 30, 60, 120]} zeroLabel={t('Off')} min={0} max={3600} /></Form.Item>
          </Col>
          <Col xs={12} sm={4}>
            <Form.Item name={['protect', 'slowloris', 'max_body_time']} label={t('Maximum upload time')} tooltip={t('Maximum time allowed to receive the entire request body')}><DurationInput presets={[60, 300, 600, 1800]} zeroLabel={t('No limit')} min={0} max={86400} /></Form.Item>
          </Col>
        </Row>
      </Card>

      <div style={{ textAlign: 'right' }}>
        <Button type="primary" onClick={handleSaveSecurity} loading={secSaving}>{t('Save access control')}</Button>
      </div>
    </Form>
  );

  // ===================== 主布局 =====================

  const tabItems = [
    {
      key: 'rules',
      label: <span><FilterOutlined />{t(' Rule settings')}</span>,
      children: renderRulesTab(),
    },
    {
      key: 'access',
      label: <span><SafetyOutlined />{t(' Access control')}</span>,
      children: renderSecurityTab(),
    },
    {
      key: 'analytics',
      label: <span><BarChartOutlined />{t(' WAF analytics')}</span>,
      children: renderAnalyticsTab(),
    },
    {
      key: 'logs',
      label: <span><UnorderedListOutlined />{t(' Event logs')}</span>,
      children: renderLogsTab(),
    },
  ];

  return (
    <div style={{ padding: isMobile ? '12px 0' : 0 }}>
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        size={isMobile ? 'small' : 'middle'}
      />
      {renderRuleModal()}
      {renderCustomRuleModal()}
    </div>
  );
}
